/* eslint-disable max-len */
import { SUBITEM_REQUIRED, SELECTION_MODE_MULTIPLE } from '_utils/constant'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Owns every piece of "what is the buyer about to purchase" state and its
 * mutation handlers. Ported 1:1 from EventDetail/index.js so quantity/limit/
 * split-case behaviour is unchanged - only re-organized into a hook.
 *
 * `productInit` arrives asynchronously (after the event/order fetch in
 * useEventDetailData resolves), so it seeds this hook's state exactly once
 * via the effect below instead of being read synchronously on mount.
 */
const useProductOrder = (productInit) => {
  const [newOrderProducts, setNewOrderProducts] = useState([])
  const [newOrderProductsIndividual, setNewOrderProductsIndividual] = useState([])
  const [productOrderRender, setProductOrderRender] = useState([])
  const [productOrderRenderIndividual, setProductOrderRenderIndividual] = useState([])
  const [quantities, setQuantities] = useState({})
  const [initTotalOrderCount, setInitTotalOrderCount] = useState(0)

  const seededRef = useRef(false)
  useEffect(() => {
    if (!productInit || seededRef.current) return
    seededRef.current = true
    setNewOrderProducts(productInit.newOrderProducts)
    setNewOrderProductsIndividual(productInit.newOrderProductsIndividual)
    setProductOrderRender(productInit.productOrderRender)
    setProductOrderRenderIndividual(productInit.productOrderRenderIndividual)
    setQuantities(productInit.quantities)
    setInitTotalOrderCount(productInit.initTotalOrderCount)
  }, [productInit])

  const findProductById = useCallback(
    (pid, index) => newOrderProducts.find((n) => n.id === pid && n.index === index),
    [newOrderProducts]
  )

  const updateOrderCount = useCallback(
    (pid, orderCount) => {
      for (let index = 0; index < productOrderRender.length; index++) {
        if (productOrderRender[index].id === pid) {
          productOrderRender[index].newOrderCount = orderCount
        }
      }
      setProductOrderRender([...productOrderRender])
    },
    [productOrderRender]
  )

  const checkMoreThanLimitPerOrder = useCallback(
    (pid, limitPerOrder) => {
      if (limitPerOrder === 0) {
        return false
      }
      const listProduct = newOrderProducts.filter((item) => item.id === pid)
      const sumQtyPerProduct = listProduct.reduce((pre, cur) => pre + cur.quantity, 0)
      return sumQtyPerProduct >= limitPerOrder
    },
    [newOrderProducts]
  )

  const checkLessThanMinOrderQty = useCallback(
    (pid, minOrderQty) => {
      const listProduct = newOrderProducts.filter((item) => item.id === pid)
      const sumQtyPerProduct = listProduct.reduce((pre, cur) => pre + cur.quantity, 0)
      return sumQtyPerProduct <= minOrderQty
    },
    [newOrderProducts]
  )

  const changeProductQuantity = useCallback(
    ({ pid, number, index, isSplit }) => {
      const productIndexNewOrder = newOrderProducts.findIndex(
        (n) => n.id === pid && n.index === index
      )
      const productIndexOrderRender = productOrderRender.findIndex(
        (n) => n.id === pid && n.index === index
      )
      const productIndexOrderRenderIndividual = productOrderRenderIndividual.findIndex(
        (n) => n.id === pid && n.index === index
      )

      if (productIndexNewOrder === -1) {
        let productFromRender = null
        if (productIndexOrderRender !== -1) {
          productFromRender = productOrderRender[productIndexOrderRender]
        } else if (productIndexOrderRenderIndividual !== -1) {
          productFromRender = productOrderRenderIndividual[productIndexOrderRenderIndividual]
        }
        if (productFromRender) {
          const newProduct = {
            id: pid,
            subItems: productFromRender.subItems
              ? productFromRender.subItems
                .filter((s) => s.required === SUBITEM_REQUIRED && !s.trackInventory && s.selectionMode !== SELECTION_MODE_MULTIPLE)
                .map((s) => ({
                  type: s.type,
                  name: s.list[0].name,
                  price: s.list[0].price,
                  required: s.required,
                }))
              : [],
            quantity: !number ? 0 : number,
            quantityInput: !number ? 0 : number,
            price: productFromRender.price,
            purchaseType: 'case',
            limitPerOrder: productFromRender.limitPerOrder,
            index,
          }
          setNewOrderProducts([...newOrderProducts, newProduct])

          if (productIndexOrderRender !== -1) {
            updateOrderCount(pid, productFromRender.newOrderCount + newProduct.quantity)
          }
          return
        }
      }
      const product = newOrderProducts[productIndexNewOrder]
      const productRender =
        productOrderRender[productIndexOrderRender] ||
        productOrderRenderIndividual[productIndexOrderRenderIndividual]
      if (isSplit) {
        const maxOrder =
          productRender.splitCasePurchase.itemsPerCase * productRender.splitCasePurchase.minItemOrder ||
          1000000
        const newOrderCount = !number ? 0 : number > maxOrder ? maxOrder : number

        newOrderProducts[productIndexNewOrder] = {
          ...product,
          splitCasePurchase: {
            ...product.splitCasePurchase,
            orderCount: newOrderCount,
          },
        }
      } else {
        const listProduct = newOrderProducts.filter((item) => item.id === pid)
        const sumQtyPerProduct = listProduct.reduce((pre, cur) => pre + cur.quantity, 0)

        const availableStock =
          productRender.maxQuantity - productRender.newOrderCount + product.quantity
        const newLimitPerOrder = productRender.limitPerOrder - sumQtyPerProduct + product.quantity

        const maxOrder =
          newLimitPerOrder === 0
            ? availableStock
            : newLimitPerOrder > availableStock
              ? availableStock
              : newLimitPerOrder

        const oldQty = product.quantity

        newOrderProducts[productIndexNewOrder] = {
          ...product,
          quantity: !number ? 0 : number > maxOrder ? maxOrder : number,
          quantityInput: !number ? 0 : number > maxOrder ? maxOrder : number,
        }

        const newQty = newOrderProducts[productIndexNewOrder].quantity

        if (newQty - oldQty > 0) {
          updateOrderCount(pid, productRender.newOrderCount + (newQty - oldQty))
        } else {
          updateOrderCount(pid, productRender.newOrderCount - (oldQty - newQty))
        }
      }

      setNewOrderProducts([...newOrderProducts])
    },
    [newOrderProducts, productOrderRender, productOrderRenderIndividual, updateOrderCount]
  )

  const changeProductQuantityIndividual = useCallback(
    ({ pid, number, index, isSplit }) => {
      const productIndexNewOrder = newOrderProductsIndividual.findIndex(
        (n) => n.id === pid && n.index === index
      )
      const productIndexOrderRender = productOrderRenderIndividual.findIndex(
        (n) => n.id === pid && n.index === index
      )
      if (productIndexNewOrder === -1) {
        const productFromRender = productOrderRenderIndividual[productIndexOrderRender]
        if (productFromRender && isSplit) {
          const newProduct = {
            id: pid,
            subItems: [],
            quantity: !number ? 0 : number,
            quantityInput: !number ? 0 : number,
            price: productFromRender.splitCasePurchase?.costPerItem || 0,
            purchaseType: 'item',
            limitPerOrder: productFromRender.limitPerOrder,
            index,
          }
          setNewOrderProductsIndividual([...newOrderProductsIndividual, newProduct])
        }
        return
      }

      const product = newOrderProductsIndividual[productIndexNewOrder]
      const productRender = productOrderRenderIndividual[productIndexOrderRender]

      if (isSplit && productRender?.splitCasePurchase) {
        const maxOrder =
          productRender.splitCasePurchase.itemsPerCase * productRender.splitCasePurchase.minItemOrder ||
          1000000
        const newOrderCount = !number ? 0 : number > maxOrder ? maxOrder : number

        const updatedProducts = [...newOrderProductsIndividual]
        updatedProducts[productIndexNewOrder] = {
          ...product,
          quantity: newOrderCount,
          quantityInput: newOrderCount,
        }
        setNewOrderProductsIndividual(updatedProducts)
      }
    },
    [newOrderProductsIndividual, productOrderRenderIndividual]
  )

  const changeProductQuantityInput = useCallback(
    ({ pid, number, index }) => {
      const productIndex = newOrderProducts.findIndex((n) => n.id === pid && n.index === index)

      if (productIndex === -1) {
        const productFromRender =
          productOrderRender.find((n) => n.id === pid && n.index === index) ||
          productOrderRenderIndividual.find((n) => n.id === pid && n.index === index)
        if (productFromRender) {
          const newProduct = {
            id: pid,
            subItems: productFromRender.subItems
              ? productFromRender.subItems
                .filter((s) => s.required === SUBITEM_REQUIRED)
                .map((s) => ({
                  type: s.type,
                  name: s.list[0].name,
                  price: s.list[0].price,
                  required: s.required,
                }))
              : [],
            quantity: 0,
            quantityInput: !number ? 0 : number,
            price: productFromRender.price,
            purchaseType: 'case',
            limitPerOrder: productFromRender.limitPerOrder,
            index,
          }
          setNewOrderProducts([...newOrderProducts, newProduct])
        }
        return
      }

      const newQuantity = !number ? 0 : number
      newOrderProducts[productIndex] = {
        ...newOrderProducts[productIndex],
        quantityInput: newQuantity,
      }
      setNewOrderProducts([...newOrderProducts])
    },
    [newOrderProducts, productOrderRender, productOrderRenderIndividual]
  )

  const getValueSelect = useCallback(
    (pid, index, type) => {
      const product = findProductById(pid, index)
      if (!product || !product.subItems) {
        return ''
      }
      const subItem = product.subItems.find((s) => s.type === type)
      if (subItem) {
        return JSON.stringify({ name: subItem.name, price: subItem.price })
      }
      return ''
    },
    [findProductById]
  )

  const handleChangeSubItem = useCallback(
    (pid, subItem, value, index) => {
      const posProduct = newOrderProducts.findIndex((n) => n.id === pid && n.index === index)
      if (posProduct === -1) return
      const listSubItem = [...(newOrderProducts[posProduct].subItems || [])]

      const isMultiple = subItem?.selectionMode === SELECTION_MODE_MULTIPLE || subItem?.trackInventory
      const targetName = value?.name
      const posSubItem = listSubItem.findIndex(
        (s) => s.type === subItem.type && (isMultiple ? (targetName ? s.name === targetName : true) : true)
      )

      if (posSubItem !== -1) {
        const checkQty = value?.quantity !== undefined ? value.quantity : value?.qty
        if (!value || (isMultiple && (checkQty === undefined || checkQty <= 0))) {
          listSubItem.splice(posSubItem, 1)
        } else if (!isMultiple) {
          listSubItem[posSubItem].price = value.price
          listSubItem[posSubItem].name = value.name
        } else {
          const qty = value.quantity !== undefined ? value.quantity : (value.qty !== undefined ? value.qty : 1)
          listSubItem[posSubItem].price = value.price
          listSubItem[posSubItem].quantity = qty
          listSubItem[posSubItem].qty = qty
        }
      } else if (value) {
        const initialQty = isMultiple
          ? (value.quantity !== undefined ? value.quantity : (value.qty !== undefined ? value.qty : 1))
          : 1
        if (initialQty > 0) {
          listSubItem.push({
            ...value,
            type: subItem.type,
            required: subItem.required,
            ...(isMultiple ? { quantity: initialQty, qty: initialQty } : {}),
          })
        }
      }
      const newOrder = [...newOrderProducts]
      newOrder[posProduct].subItems = listSubItem
      if (subItem?.trackInventory) {
        const totalSubQty = listSubItem.reduce(
          (acc, s) => acc + parseInt(s.quantity ?? s.qty ?? 0, 10),
          0
        )
        newOrder[posProduct].quantity = totalSubQty
        newOrder[posProduct].quantityInput = totalSubQty
      }
      setNewOrderProducts(newOrder)
    },
    [newOrderProducts]
  )

  const addAnotherOrder = useCallback(
    (pid, products) => {
      const lastIndex = productOrderRender.map((item) => item.id).lastIndexOf(pid)
      const lastItem = productOrderRender[lastIndex]
      const sameInRender = productOrderRender.filter((it) => it.id === pid)
      const sameInIndividual = productOrderRenderIndividual.filter((it) => it.id === pid)
      const maxRenderIndex = sameInRender.reduce((m, it) => Math.max(m, Number(it.index || 0)), 0)
      const maxIndividualIndex = sameInIndividual.reduce((m, it) => Math.max(m, Number(it.index || 0)), 0)
      let baseIndex = lastItem?.index
      if (!baseIndex) {
        baseIndex = Math.max(maxRenderIndex, maxIndividualIndex, 0)
      }
      const newIndex = baseIndex + 1

      const template = products.find((item) => item.id === pid) || {}
      const product = {
        ...template,
        splitCasePurchase: null,
        index: newIndex,
        newOrderCount: lastItem?.newOrderCount ?? template.orderCount ?? 0,
      }

      const insertPos = lastIndex === -1 ? productOrderRender.length - 1 : lastIndex
      const newProductOrderRender = [...productOrderRender]
      newProductOrderRender.splice(insertPos + 1, 0, product)

      const newOrderProductConvert = {
        id: product.id,
        subItems: (product.subItems || [])
          .filter((s) => s.required === SUBITEM_REQUIRED)
          .map((s) => ({ type: s.type, name: s.list[0]?.name, price: s.list[0]?.price })),
        quantity: 1,
        quantityInput: 1,
        price: product.price,
        limitPerOrder: product.limitPerOrder,
        minOrderQty: product.minOrderQty,
        index: newIndex,
        splitCasePurchase: null,
      }

      setProductOrderRender(newProductOrderRender)
      setNewOrderProducts((prev) => [...prev, newOrderProductConvert])

      if (Number(maxRenderIndex) < Number(maxIndividualIndex) && productOrderRenderIndividual.length > 1) {
        setTimeout(() => {
          if (typeof window !== 'undefined' && window.scrollTo) {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        }, 80)
      }
    },
    [productOrderRender, productOrderRenderIndividual]
  )

  const deleteItemOrder = useCallback(
    (pid, index) => {
      const indexOrderProducts = newOrderProducts.findIndex(
        (item) => item.id === pid && item.index === index
      )
      const indexProductOrderRender = productOrderRender.findIndex(
        (item) => item.id === pid && item.index === index
      )

      for (let i = 0; i < productOrderRender.length; i++) {
        if (productOrderRender[i].id === pid) {
          productOrderRender[i].newOrderCount -= newOrderProducts[indexOrderProducts].quantity
        }
      }

      newOrderProducts.splice(indexOrderProducts, 1)
      productOrderRender.splice(indexProductOrderRender, 1)

      setNewOrderProducts([...newOrderProducts])
      setProductOrderRender([...productOrderRender])
    },
    [newOrderProducts, productOrderRender]
  )

  return {
    newOrderProducts,
    setNewOrderProducts,
    newOrderProductsIndividual,
    setNewOrderProductsIndividual,
    productOrderRender,
    productOrderRenderIndividual,
    quantities,
    setQuantities,
    initTotalOrderCount,
    findProductById,
    checkMoreThanLimitPerOrder,
    checkLessThanMinOrderQty,
    changeProductQuantity,
    changeProductQuantityIndividual,
    changeProductQuantityInput,
    getValueSelect,
    handleChangeSubItem,
    addAnotherOrder,
    deleteItemOrder,
  }
}

export default useProductOrder
